import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import {
  ActionTabItem,
  largeNuggetVariantMapper,
  FullScreenLoading,
  getTypeChipPropsForVariant,
  NotFound404,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { useGetNuggetActionParams } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.hook';
import {
  determineTitle,
  determineSubtitle,
  determineDescription,
  nuggetMapper,
} from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { selectContentNuggets } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useGetContentNuggetsByCompetencySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import {
  fetchOneContentNuggetBySlug,
  searchForCareerPathById,
  searchForContentScholarship,
  searchForProfileInternalById,
  searchForPostById,
  searchForContentProfileExternalById,
  searchForGeneratedContentByUuid,
} from '@backrs_ui/grow-ui/src/redux/contentNugget/thunks/oneNuggetBySlug';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { useAgentContext } from './NuggetPage.agentContext';
import { getCompetencySlugsFromNugget } from './nuggetPage.util';
import { NuggetPageContent } from './NuggetPageContent';

export const NuggetPage = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { currentDialogNugget, loading, error } = useAppSelector(selectContentNuggets);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const { actionParams } = useGetNuggetActionParams(currentDialogNugget || null);

  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  useAgentContext(currentDialogNugget);

  const internshipId = searchParams.get('internship');

  const competencySlugs = getCompetencySlugsFromNugget(currentDialogNugget, contentCompetencies);

  const {
    data: allSuggestedNuggets = [],
    isLoading: isLoadingSuggestedNuggets,
    error: suggestedNuggetsError,
  } = useGetContentNuggetsByCompetencySlugsQuery(competencySlugs, {
    skip: competencySlugs.length === 0,
    refetchOnMountOrArgChange: true,
  });

  // filter out the nugget we are currently viewing & if no competency slugs, return empty array to clear suggestions
  const suggestedNuggets =
    competencySlugs.length === 0
      ? []
      : allSuggestedNuggets.filter((nugget) => nugget.id !== currentDialogNugget?.id);

  useEffect(() => {
    if (internshipId) return;

    const scholarshipId = searchParams.get('scholarship');
    const profileInternalId = searchParams.get('profile-in');
    const profileExternalId = searchParams.get('profile-ex');
    const postId = searchParams.get('post');
    const generatedContentId = searchParams.get('generated-content');
    const careerPathId = searchParams.get('career-path');
    const nuggetId = searchParams.get('nugget');

    if (scholarshipId) {
      dispatch(searchForContentScholarship({ search_term: scholarshipId }));
    } else if (profileInternalId) {
      dispatch(searchForProfileInternalById({ id: profileInternalId }));
    } else if (profileExternalId) {
      dispatch(searchForContentProfileExternalById({ search_term: profileExternalId }));
    } else if (postId) {
      dispatch(searchForPostById({ id: postId }));
    } else if (generatedContentId) {
      dispatch(searchForGeneratedContentByUuid({ uuid: generatedContentId }));
    } else if (careerPathId) {
      dispatch(searchForCareerPathById({ id: careerPathId }));
    } else if (nuggetId) {
      dispatch(fetchOneContentNuggetBySlug({ slug: nuggetId }));
    }
  }, [dispatch, internshipId, searchParams]);

  usePageTitle(currentDialogNugget ? determineTitle(currentDialogNugget) : undefined);

  if (internshipId) {
    return <Navigate to={`/internship/${internshipId}`} replace />;
  }

  if (loading) {
    return <FullScreenLoading />;
  }

  if (error || !currentDialogNugget) {
    return <NotFound404 />;
  }

  // get title, subtitle, and description
  const title = determineTitle(currentDialogNugget);
  const subtitle = determineSubtitle(currentDialogNugget) || '';
  const description = determineDescription(currentDialogNugget);

  const nuggetVariant = nuggetMapper({ nugget: currentDialogNugget });
  const { component } = largeNuggetVariantMapper(nuggetVariant);

  const hasVisualMedia =
    ('url' in nuggetVariant && !!nuggetVariant.url) ||
    ('previewImage' in nuggetVariant && !!nuggetVariant.previewImage);

  // create action button group
  const buttonGroup: ActionTabItem[] = [];

  if (actionParams?.handleActionBookmark) {
    buttonGroup.push({
      onClick: actionParams.handleActionBookmark,
      icon: actionParams.hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
      tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
    });
  }

  if (actionParams?.handleActionShare) {
    buttonGroup.push({
      onClick: actionParams.handleActionShare,
      icon: 'Share06',
      tooltipOnClickText: 'Link copied!',
      tooltipHoverText: commonAppStrings.modules.actionTabs.share,
    });
  }

  if (actionParams?.handleActionAddTask) {
    buttonGroup.push({
      onClick: actionParams.handleActionAddTask,
      icon: 'BookmarkAdd',
      tooltipHoverText: commonAppStrings.modules.actionTabs.task,
    });
  }

  return (
    <NuggetPageContent
      title={title}
      subtitle={subtitle}
      description={description}
      children={component}
      hasVisualMedia={hasVisualMedia}
      buttonGroup={buttonGroup}
      showCopySuccess={showCopySuccess}
      onCopySuccessChange={setShowCopySuccess}
      suggestedNuggets={suggestedNuggets}
      isLoadingSuggestedNuggets={isLoadingSuggestedNuggets}
      suggestedNuggetsError={suggestedNuggetsError}
      typeChipProps={getTypeChipPropsForVariant(nuggetVariant)}
    />
  );
};
